import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import Brand from "../components/Brand";
import Seo from "../components/Seo";
import {
  ArrowLeft, ExternalLink, Zap, Flag, Wrench, Trophy,
  ArrowRight, CheckCircle2, Shirt, Award, Rocket,
} from "lucide-react";

/* /rc-racers — public affiliate landing page for Mike's BeachRC
   (remote control cars / trucks / apparel) affiliate program.
   Every "Shop" button opens AFFILIATE_URL in a new tab.

   The route lives at /rc-racers (and /rcracers) because the page name
   describes the hobby, not the storefront. Mike sells through BeachRC
   (aff=1841) — see AFFILIATE_URL below. */

const G        = "#2E9F3A";
const O        = "#F39A1F";
const WINE     = "#0F172A";
const TRACK    = "#0F1116";   // race-track black
const NEON     = "#D4FF00";   // electric yellow-green
const RACE_RED = "#E11929";

/* Mike's BeachRC affiliate link — every Shop button on this page routes
   through this single constant. */
const AFFILIATE_URL = "https://www.beachrc.com?aff=1841";

/* Imagery Mike shared (already hosted on Emergent customer-assets). */
const TLR_COLLAGE   = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/1pxqz8cn_Screenshot%202026-05-17%20123913.png";  // TLR Team Losi Apex collage (hero)
const SCALE_BANNER  = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/b41l50o1_Screenshot%202026-05-17%20123954.png";  // SCALE & CRAWLERS official banner
const STAMPEDE_RED  = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/7eht94hb_Screenshot%202026-05-17%20125957.png";  // Traxxas Stampede red basher
const CHEVELLE_BLUE = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/r1rqopt7_Screenshot%202026-05-17%20125924.png";  // Race-Inspired blue Chevelle on-road
const POLO_SHIRT    = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/4s1hn0v2_Screenshot%202026-05-17%20124154.png";  // black/red polo
const SWORKZ_TEE    = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/vgcm6zv1_Screenshot%202026-05-17%20124146.png";  // red SWorkz tee
const WHEEL_HAT     = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/jjjh10w1_Screenshot%202026-05-17%20124116.png";  // Wheel Triggers trucker hat

const CATEGORIES = [
  {
    key: "race",
    name: "Race Buggies",
    tagline: "Team Losi · TLR · The Pinnacle Of Off-Road",
    blurb: "Top-tier 1/10 race buggies — 22X Dirt/Clay, 22X Astro/Carpet, and the full Team Losi competition lineup. The chassis the podium runs on.",
    badge: "★ COMPETITION GRADE",
    badgeColor: NEON,
    accent: NEON,
    img: TLR_COLLAGE,
  },
  {
    key: "crawler",
    name: "Scale & Crawlers",
    tagline: "Vehicles For The Terrain",
    blurb: "Trail rigs, Bronco-style scale crawlers, rock-ready 4x4s. Built to look real and climb anything. Slow, deliberate driving — the opposite of bashing.",
    badge: "★ TRAIL READY",
    badgeColor: RACE_RED,
    accent: RACE_RED,
    img: SCALE_BANNER,
  },
  {
    key: "basher",
    name: "Bashers",
    tagline: "Traxxas Stampede · Built To Take Hits",
    blurb: "Tough, jump-it-off-anything monster trucks like the iconic Traxxas Stampede. Hits hard, takes the hits back. The category most newcomers fall in love with first.",
    badge: "★ BUILT TO TAKE HITS",
    badgeColor: O,
    accent: O,
    img: STAMPEDE_RED,
  },
  {
    key: "street",
    name: "Street & Drag",
    tagline: "Race-Inspired On-Road",
    blurb: "Classic muscle bodies, drag cars, and on-road race-inspired rigs. Burn rubber, lay stripes, look like the real thing. The hobby's most aesthetic category.",
    badge: "★ MUSCLE & SPEED",
    badgeColor: "#3B7DD8",
    accent: "#3B7DD8",
    img: CHEVELLE_BLUE,
  },
];

const APPAREL = [
  {
    key: "polo",
    name: "Team Polo · Race Day",
    tagline: "Pit Crew Approved",
    blurb: "Black & red official team polo — Xceed / UR Racing patches embroidered. The shirt you wear when somebody important is watching.",
    img: POLO_SHIRT,
    accent: RACE_RED,
  },
  {
    key: "sworkz",
    name: "SWorkz Race Tee",
    tagline: "Red · Bold · Loud",
    blurb: "Official SWorkz team tee in race-day red. Wear it to the track, the parking lot, or anywhere you want the world to know which team you're on.",
    img: SWORKZ_TEE,
    accent: RACE_RED,
  },
  {
    key: "hat",
    name: "Wheel Triggers Trucker Hat",
    tagline: "Pit-Stop Essential",
    blurb: "Classic mesh-back trucker — black crown, teal stripe, embroidered Wheel Triggers logo. The hat that lives in your truck for race weekends.",
    img: WHEEL_HAT,
    accent: "#3FBFC4",
  },
];

const PILLARS = [
  { Icon: Zap,     title: "Lightning Performance", body: "Top brands like TLR, SWorkz, Xceed — the chassis and components that actually podium." },
  { Icon: Wrench,  title: "Built To Rebuild",     body: "Hop-up parts, spares, tires, bodies — everything you need to keep your rig running every weekend." },
  { Icon: Flag,    title: "Race-Ready Out Of Box", body: "RTRs, kits, and pro-builds. From beginner basher to club racer to national-level competitor." },
  { Icon: Trophy,  title: "Mike's Personal Pick",  body: "I race these myself. If RC isn't on your radar yet — buckle up. It's the best father-son hobby on earth." },
];

export default function RcRacersPage() {
  const nav = useNavigate();
  const [imgZoom, setImgZoom] = useState(null);

  const shop = () => window.open(AFFILIATE_URL, "_blank", "noopener,noreferrer");

  return (
    <div className="min-h-screen" style={{ background: "#FFFFFF" }} data-testid="rc-racers-page">
      <Seo
        title="BeachRC — Remote Control Cars, Trucks & Race Gear"
        description="Mike's pick for serious RC: Team Losi (TLR) race buggies, scale crawlers, Traxxas bashers, race-inspired street/drag rigs, and official team apparel through BeachRC. Faitharama members earn 25%/50% on every order."
        canonical="https://faitharama.com/rc-racers"
        image={TLR_COLLAGE}
        keywords="RC cars, RC trucks, Team Losi, TLR buggy, scale crawler, Traxxas Stampede basher, drag car, SWorkz, BeachRC, Xceed, remote control racing, RC apparel"
        schema={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "BeachRC — Remote Control Cars, Trucks & Race Gear",
          "description": "Race buggies, scale crawlers, bashers, race-inspired street rigs, hop-up parts, and official team apparel.",
          "brand": { "@type": "Brand", "name": "BeachRC" }
        }}
      />
      <SiteNav />

      {/* ─────── HERO ─────── */}
      <section className="relative overflow-hidden" data-testid="rc-hero"
               style={{ background: `linear-gradient(180deg, ${TRACK} 0%, #1A1D26 60%, ${TRACK} 100%)` }}>
        {/* Checker-flag accent strip */}
        <div className="absolute top-0 left-0 right-0 h-2"
             style={{ backgroundImage: `linear-gradient(45deg, ${NEON} 25%, transparent 25%, transparent 50%, ${NEON} 50%, ${NEON} 75%, transparent 75%, transparent)`,
                      backgroundSize: "24px 24px" }}/>

        <div className="max-w-7xl mx-auto px-5 md:px-8 py-10 md:py-14 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] mb-4 hover:opacity-80"
                    data-testid="rc-back" style={{ color: NEON }}>
              <ArrowLeft className="w-4 h-4"/> Back
            </button>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.32em] uppercase font-bold shadow"
                 style={{ background: NEON, color: TRACK }}>
              ★ NEW PARTNER · LIVE NOW
            </div>
            <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl uppercase leading-[0.95] mt-4"
                style={{ color: "#FFFFFF", textWrap: "balance", letterSpacing: "-0.01em" }}>
              Race. <span style={{ color: NEON }}>Bash.</span> Crawl.
            </h1>
            <p className="mt-5 text-lg md:text-xl leading-relaxed max-w-xl"
               style={{ color: "rgba(255,253,247,0.92)" }}>
              Mike just partnered with <strong style={{ color: NEON }}>BeachRC</strong> — the
              one-stop shop for serious remote-control racing.
              Team Losi buggies, Bronco-style scale crawlers, Traxxas bashers, race-inspired street rigs, and the team apparel to back it up.
            </p>
            <p className="mt-3 text-base md:text-lg leading-relaxed max-w-xl italic"
               style={{ color: "rgba(255,253,247,0.78)" }}>
              Whether you're 12 or 72, this is the hobby that gets everyone off the couch and out into the yard. A community of folks already hooked — now you can be too.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button onClick={shop} data-testid="rc-hero-shop"
                      className="inline-flex items-center gap-2 px-6 py-4 rounded-md text-base font-bold uppercase tracking-[0.16em] shadow-2xl hover:scale-[1.03] transition"
                      style={{ background: NEON, color: TRACK }}>
                Shop BeachRC <ExternalLink className="w-5 h-5"/>
              </button>
              <a href="#categories"
                 className="inline-flex items-center gap-2 px-6 py-4 rounded-md text-base font-bold uppercase tracking-[0.16em] border-2 hover:bg-white/10 transition"
                 style={{ borderColor: NEON, color: NEON }}>
                See What's Inside <ArrowRight className="w-5 h-5"/>
              </a>
            </div>
            <div className="mt-6 flex items-center gap-3 text-xs md:text-sm"
                 style={{ color: "rgba(255,253,247,0.7)" }}>
              <Flag className="w-4 h-4" style={{ color: NEON }}/>
              <span>Free Faitharama members earn <strong style={{ color: NEON }}>50%</strong> · Paid Builders &amp; Pros earn <strong style={{ color: NEON }}>50%</strong> on every RC sale they refer.</span>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-3 rounded-2xl blur-2xl opacity-50"
                 style={{ background: `radial-gradient(closest-side, ${NEON}, transparent 70%)` }}/>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4"
                 style={{ borderColor: NEON }}>
              <img src={TLR_COLLAGE} alt="Team Losi Racing — TLR 22X buggies in Dirt/Clay and Astro/Carpet trims"
                   className="block w-full h-auto" loading="eager"/>
            </div>
          </div>
        </div>
      </section>

      {/* ─────── 4 CATEGORIES ─────── */}
      <section id="categories" className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20" data-testid="rc-categories">
        <div className="text-center mb-9">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>
            ★ FOUR WAYS TO RIDE
          </div>
          <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-[1.05] mt-1"
              style={{ color: TRACK, textWrap: "balance" }}>
            Pick Your Lane.
          </h2>
          <p className="mt-3 text-base md:text-lg max-w-2xl mx-auto" style={{ color: "#0F172A" }}>
            Race buggies, scale crawlers, bashers, or street/drag. <strong>BeachRC</strong> stocks all four — and the parts to keep them running every weekend.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {CATEGORIES.map((c) => (
            <button key={c.key} onClick={shop}
                    className="text-left rounded-2xl overflow-hidden bg-white border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition cursor-pointer"
                    data-testid={`rc-cat-${c.key}`}
                    style={{ borderColor: c.accent }}>
              <div className="relative aspect-[5/4] overflow-hidden flex items-center justify-center"
                   style={{ background: TRACK }}>
                <img src={c.img} alt={`${c.name} — ${c.tagline}`}
                     className="block max-w-full max-h-full w-auto h-auto object-contain hover:scale-[1.04] transition-transform duration-500"
                     loading="lazy"/>
                <span className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2.5 py-1.5 rounded-full shadow-md"
                      style={{ background: c.badgeColor, color: c.badgeColor === NEON ? TRACK : "#FFFFFF" }}>
                  {c.badge}
                </span>
              </div>
              <div className="p-5 md:p-6">
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: c.accent === NEON ? "#7A8A00" : c.accent }}>
                  {c.tagline}
                </div>
                <div className="font-serif-display text-2xl md:text-3xl mt-1" style={{ color: TRACK, fontWeight: 700 }}>
                  {c.name}
                </div>
                <p className="text-base mt-2 leading-relaxed" style={{ color: "#0F172A" }}>
                  {c.blurb}
                </p>
                <div className="mt-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em]"
                     style={{ color: c.accent === NEON ? "#7A8A00" : c.accent }}>
                  Shop {c.name} <ArrowRight className="w-4 h-4"/>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ─────── 4 PILLARS ─────── */}
      <section className="py-12 md:py-16" style={{ background: "#EAF6FC" }} data-testid="rc-pillars">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="text-center mb-9">
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: WINE }}>
              ★ WHY BEACHRC
            </div>
            <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-[1.05] mt-1"
                style={{ color: TRACK, textWrap: "balance" }}>
              Built For Drivers,<br/>Not Tourists.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {PILLARS.map((p) => (
              <div key={p.title} className="bg-white rounded-xl p-5 md:p-6 shadow-sm border-2"
                   style={{ borderColor: `${O}33` }}>
                <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white shadow"
                     style={{ background: TRACK }}>
                  <p.Icon className="w-6 h-6"/>
                </div>
                <h3 className="font-serif-display text-xl mt-3 leading-tight" style={{ color: TRACK, fontWeight: 700 }}>
                  {p.title}
                </h3>
                <p className="text-base mt-1.5 leading-relaxed" style={{ color: "#0F172A" }}>
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────── APPAREL ─────── */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20" data-testid="rc-apparel">
        <div className="text-center mb-9">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.28em] uppercase font-bold shadow"
               style={{ background: TRACK, color: NEON }}>
            <Shirt className="w-3.5 h-3.5"/> RACE-DAY APPAREL
          </div>
          <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-[1.05] mt-3"
              style={{ color: TRACK, textWrap: "balance" }}>
            Wear The <span style={{ color: RACE_RED }}>Team.</span>
          </h2>
          <p className="mt-3 text-base md:text-lg max-w-2xl mx-auto" style={{ color: "#0F172A" }}>
            Official team apparel — polos for the pits, tees for the track, gear that shows up before you do.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {APPAREL.map((a) => (
            <button key={a.key} onClick={shop}
                    className="text-left rounded-2xl overflow-hidden bg-white border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition cursor-pointer"
                    data-testid={`rc-apparel-${a.key}`}
                    style={{ borderColor: a.accent }}>
              <div className="relative aspect-square overflow-hidden flex items-center justify-center"
                   style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #E0F2FE 100%)" }}>
                <img src={a.img} alt={`${a.name} — ${a.tagline}`}
                     className="block max-h-[88%] w-auto object-contain hover:scale-[1.04] transition-transform duration-500 drop-shadow-2xl"
                     loading="lazy"/>
              </div>
              <div className="p-5 md:p-6">
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: a.accent }}>
                  {a.tagline}
                </div>
                <div className="font-serif-display text-2xl mt-1" style={{ color: TRACK, fontWeight: 700 }}>
                  {a.name}
                </div>
                <p className="text-base mt-2 leading-relaxed" style={{ color: "#0F172A" }}>
                  {a.blurb}
                </p>
                <div className="mt-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em]"
                     style={{ color: a.accent }}>
                  Shop This <ExternalLink className="w-4 h-4"/>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ─────── EARNINGS BLOCK ─────── */}
      <section className="py-14 md:py-20" style={{ background: TRACK }} data-testid="rc-earnings">
        <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.32em] uppercase font-bold shadow"
               style={{ background: NEON, color: TRACK }}>
            <Award className="w-3.5 h-3.5"/> SHARE IT · EARN ON IT
          </div>
          <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-[1.05] mt-3"
              style={{ color: "#FFFFFF", textWrap: "balance" }}>
            Everyone Earns <span style={{ color: NEON }}>50%</span><br/>
            Paid Members Earn <span style={{ color: NEON }}>50%</span>
          </h2>
          <p className="mt-4 text-base md:text-lg max-w-2xl mx-auto leading-relaxed"
             style={{ color: "rgba(255,253,247,0.82)" }}>
            Same Faitharama promise — flat rate, recurring, forever. Share your Mirror Site URL with one RC buddy and you earn on every cart they fill. No tier hoops. No  thresholds.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <button onClick={shop} data-testid="rc-earnings-shop"
                    className="inline-flex items-center gap-2 px-6 py-4 rounded-md text-base font-bold uppercase tracking-[0.16em] shadow-2xl hover:scale-[1.03] transition"
                    style={{ background: NEON, color: TRACK }}>
              Shop BeachRC Now <ExternalLink className="w-5 h-5"/>
            </button>
            <Link to="/signup?plan=free"
                  className="inline-flex items-center gap-2 px-6 py-4 rounded-md text-base font-bold uppercase tracking-[0.16em] border-2 hover:bg-white/10 transition"
                  style={{ borderColor: NEON, color: NEON }}>
              Join Faitharama Free <ArrowRight className="w-5 h-5"/>
            </Link>
          </div>
        </div>
      </section>

      {/* ─────── FINAL CTA ─────── */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-14 md:py-20 text-center" data-testid="rc-final-cta">
        <Rocket className="w-12 h-12 mx-auto" style={{ color: O }}/>
        <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-[1.05] mt-3"
            style={{ color: WINE, textWrap: "balance" }}>
          Ready To Take The Green Flag?
        </h2>
        <p className="mt-4 text-base md:text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: "#0F172A" }}>
          One stop. Buggies, crawlers, bashers, parts, and apparel. A trusted partner — so we get our members in the door, and you get to share the brand with everyone in your RC circle.
        </p>
        <div className="mt-6">
          <button onClick={shop} data-testid="rc-final-shop"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-md text-lg font-bold uppercase tracking-[0.16em] text-white shadow-2xl hover:scale-[1.03] transition"
                  style={{ background: TRACK }}>
            Shop BeachRC <ExternalLink className="w-5 h-5"/>
          </button>
        </div>
        <p className="mt-5 text-xs md:text-sm italic text-foreground/65 max-w-3xl mx-auto leading-relaxed">
          Mike earns a small commission on every BeachRC order placed through this page (affiliate ID 1841). Your price doesn't change — BeachRC pays Mike directly. Thank you for supporting <Brand /> with every purchase.
        </p>
      </section>

      <SiteFooter />

      {/* Optional click-to-zoom for the apparel/category imagery */}
      {imgZoom && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6"
             onClick={() => setImgZoom(null)}>
          <img src={imgZoom} alt="Zoom" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"/>
        </div>
      )}
    </div>
  );
}
